'use client'

import { useEffect, useState } from 'react'

interface ConditionalJQueryLoaderProps {
  /** Load jQuery immediately (for pages with forms) */
  immediate?: boolean
}

/**
 * Conditionally loads jQuery based on page requirements
 *
 * Usage:
 * - For pages with HubSpot forms: <ConditionalJQueryLoader immediate />
 * - For other pages: Uses global lazyOnload strategy
 */
export default function ConditionalJQueryLoader({ immediate = false }: ConditionalJQueryLoaderProps) {
  const [shouldLoad, setShouldLoad] = useState(immediate)
  const scriptId = 'jquery-conditional'

  useEffect(() => {
    // If jQuery is already loaded, don't load it again
    if (typeof window !== 'undefined' && window.jQuery) {
      return
    }

    // For non-immediate loading, wait for user interaction
    if (!immediate) {
      const handleInteraction = () => {
        setShouldLoad(true)
      }

      const events = ['mousemove', 'scroll', 'touchstart', 'click']
      events.forEach((event) => {
        window.addEventListener(event, handleInteraction, { passive: true, once: true })
      })

      // Cleanup
      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, handleInteraction)
        })
      }
    }
  }, [immediate])

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldLoad || window.jQuery) {
      return
    }

    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null
    if (existingScript) {
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = 'https://ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js'
    script.async = true
    script.defer = !immediate

    const appendScript = () => {
      if (!document.getElementById(scriptId)) {
        document.body.appendChild(script)
      }
    }

    if (immediate || document.readyState === 'complete') {
      appendScript()
      return
    }

    window.addEventListener('load', appendScript, { once: true })
    return () => {
      window.removeEventListener('load', appendScript)
    }
  }, [immediate, shouldLoad, scriptId])

  return null
}
